import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { DynamicDialogProvider } from "../components/DynamicDialogProvider";
import MaintenanceRegistryTable from "../components/MaintenanceRegistryTable";
import {
  MAINTENANCE_TABS,
  maintenanceRegistryDummyData,
} from "../config/maintenanceRegistryConfig";
import { useAmsModuleData } from "../context/AmsModuleDataContext";

const CATEGORY_BY_TAB = {
  equipments: "equipment",
  valves: "valve",
  instruments: "instrument",
};

/**
 * Read the MCS CPI `CategoryType` from a merged material spec object.
 * Values seen in the data: "Valve", "Instrument", "Equipment", "Bulk".
 */
function getCategoryType(specs) {
  if (!specs || typeof specs !== "object") return "";
  const raw =
    specs.CategoryType ??
    specs.categoryType ??
    specs["Category Type"] ??
    specs.category_type;
  return String(raw ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Does a material's CategoryType belong to the active tab?
 * - valves tab       -> "valve"
 * - instruments tab  -> "instrument"
 * - equipments tab   -> everything that is not a valve or instrument
 *   (e.g. "equipment", "bulk", or missing category)
 */
function matchesCategory(targetCategory, categoryType) {
  if (targetCategory === "valve") return categoryType === "valve";
  if (targetCategory === "instrument") return categoryType === "instrument";
  if (targetCategory === "equipment") {
    return categoryType !== "valve" && categoryType !== "instrument";
  }
  return true;
}

function pickSpecsDataSheet(specs) {
  if (!specs || typeof specs !== "object") return undefined;
  const v =
    specs.dataSheet ??
    specs.dataSheets ??
    specs["Data Sheet"] ??
    specs["Data Sheets"];
  return v !== undefined && v !== null && v !== "" ? v : undefined;
}

function normalizeTagKey(tag) {
  return String(tag ?? "")
    .trim()
    .toLowerCase();
}

/** Turn trimListTable slot into a list of row objects (array or single object). */
function trimListSlotToRows(slot) {
  if (slot == null) return [];
  if (Array.isArray(slot)) return slot;
  if (typeof slot === "object") return [slot];
  return [];
}

/**
 * From MCS project shape: revisions[].data.pnidData[].trimListTable
 * - [0]: material rows (Tag Number / TagNumber)
 * - [1]: rows with Tag Number + dataSheet, matched to materials by tag
 * Returns Map<normalizedTag, { trimMaterial, dataSheet }>
 */
function buildMcsTrimListByTag(project) {
  /** @type {Map<string, { trimMaterial: object, dataSheet?: * }>} */
  const byTag = new Map();
  const allTrimListMaterials = [];

  const revisions = Array.isArray(project?.revisions) ? project.revisions : [];

  revisions.forEach((rev) => {
    const rawPnidData = rev?.data?.pnidData;
    const pnidArr = Array.isArray(rawPnidData)
      ? rawPnidData
      : rawPnidData && typeof rawPnidData === "object"
        ? Object.values(rawPnidData)
        : [];

    pnidArr.forEach((pnidEntry) => {
      const tbl = pnidEntry?.trimListTable;
      if (!Array.isArray(tbl) || tbl.length === 0) return;

      // Collect all material-like rows from every trimListTable slot.
      // Source of truth for dataSheet should come from these material objects.
      const rowsAcrossSlots = tbl.flatMap((slot) => trimListSlotToRows(slot));
      allTrimListMaterials.push(...rowsAcrossSlots);
    });
  });

  // console.log("allTrimListMaterials", allTrimListMaterials);

  allTrimListMaterials.forEach((mat) => {
    const tagKey = normalizeTagKey(mat?.["Tag Number"] ?? mat?.TagNumber);
    if (!tagKey) return;

    const dataSheet =
      mat?.dataSheet ??
      mat?.dataSheets ??
      mat?.["Data Sheet"] ??
      mat?.["Data Sheets"] ??
      undefined;

    const prev = byTag.get(tagKey);
    byTag.set(tagKey, {
      trimMaterial: prev?.trimMaterial
        ? { ...prev.trimMaterial, ...mat }
        : { ...mat },
      dataSheet: prev?.dataSheet ?? dataSheet,
    });
  });

  return byTag;
}

function normalizeSpareParts(spareParts = []) {
  return spareParts.map((item, index) => ({
    id: item?.id || `sp-${index}`,
    vendorPartNo: item?.["Vendor Part #"] || "",
    partDescription: item?.["Part Description"] || "",
    cost: item?.Cost || "",
    deliverySchedule: item?.["Delivery Schedule"] || "",
    shelfLife: item?.["Shelf Life"] || "",
    status: item?.Status || "",
    qty: item?.Qty || "",
    remarks: item?.Remarks || "",
  }));
}

function normalizeInspectionFindingRows(itps = [], tagNo = "") {
  const normalizedTag = String(tagNo || "")
    .trim()
    .toLowerCase();

  if (!normalizedTag) return [];

  return (Array.isArray(itps) ? itps : [])
    .filter(
      (item) =>
        String(item?.tagNumber || "")
          .trim()
          .toLowerCase() === normalizedTag,
    )
    .map((item, index) => {
      const normalizedFlag = String(item?.flag || "")
        .trim()
        .toLowerCase();

      return {
        id: item?.id || `itp-${normalizedTag}-${index}`,
        rawItp: item,
        actions: "Open",
        no: index + 1,
        inspectionTestActivity: item?.inspectionTestActivities || "",
        rev: item?.rev || "",
        controllingDocument: item?.controllingDoc || "",
        acceptanceCriteria: item?.acceptanceCriteria || "",
        recordingDocument: item?.recordingDoc || "",
        startDate: item?.startDate || "",
        endDate: item?.endDate || "",
        progress: item?.progress || "",
        actualHrs: item?.actualHours || "",
        authorizedInspectorType: item?.authorizedInspectionFunc || "",
        authorizedInspectorSign: item?.authorizedInspectionInitials || "",
        authorizedInspectorDate: item?.authorizedInspectionDate || "",
        shopType: item?.representativeFunc || "",
        shopSign: item?.representativeInitials || "",
        shopDate: item?.representativeDate || "",
        clientType: item?.cdtFunc || "",
        clientSign: item?.cdtInitials || "",
        clientDate: item?.cdtDate || "",
        conclusion: item?.specificInstruction || "",
        flag:
          normalizedFlag === "red" ||
          normalizedFlag === "yellow" ||
          normalizedFlag === "green"
            ? normalizedFlag
            : "",
      };
    });
}

function buildRowsFromProjects(
  projects = [],
  activeTabKey,
  dummyTemplates = [],
  itps = [],
) {
  const targetCategory = CATEGORY_BY_TAB[activeTabKey];
  // console.log("AIMS targetCategory:", targetCategory);

  if (!targetCategory) return [];

  const rows = [];
  let templateIndex = 0;

  projects.forEach((project) => {
    const mcsTrimByTag = buildMcsTrimListByTag(project);

    const drawings = Array.isArray(project?.drawings) ? project.drawings : [];
    drawings
      .filter((drawing) => drawing?.source === "Trimlist")
      .forEach((drawing) => {
        const materials = Array.isArray(drawing?.materials)
          ? drawing.materials
          : [];

        console.log(
          "AIMS materials",
          targetCategory,
          materials.filter(
            (m) => m.categoryType?.toLowerCase() === targetCategory,
          ),
        );
        materials
          .filter(
            (m) =>
              m.description &&
              m.description !== "-" &&
              m.categoryType?.toLowerCase() === targetCategory,
          )
          .forEach((materialItem, materialIndex) => {
            const tagNumber = materialItem?.tagNumber;
            console.log("tagNumber", tagNumber);
            if (!tagNumber) return;

            const tagNo = tagNumber || "-";
            const mcs = mcsTrimByTag.get(normalizeTagKey(tagNo));

            const mergedSpecs = {
              ...(mcs?.trimMaterial && typeof mcs.trimMaterial === "object"
                ? mcs.trimMaterial
                : {}),
              ...(materialItem?.specs?.spec &&
              typeof materialItem?.specs?.spec === "object"
                ? materialItem?.specs?.spec
                : {}),
            };

            // Filter by MCS CPI CategoryType so each tab only shows its own
            // items (valves / instruments / equipment).
            // if (
            //   !matchesCategory(targetCategory, getCategoryType(mergedSpecs))
            // ) {
            //   return;
            // }

            const template =
              dummyTemplates[
                templateIndex % Math.max(dummyTemplates.length, 1)
              ] || {};

            templateIndex += 1;

            const materialDescription =
              (materialItem?.description === "undefined undefined" &&
                "Vessel") ||
              materialItem?.description ||
              materialItem?.specs?.spec?.["Material Description"] ||
              mcs?.trimMaterial?.["Material Description"] ||
              "-";
            const component =
              materialDescription?.split(",")[0] ||
              materialItem?.component ||
              materialItem?.specs?.spec?.Component ||
              mcs?.trimMaterial?.Component ||
              "-";
            rows.push({
              ...template,
              id:
                materialItem?.id ||
                `${project?.id || "project"}-${
                  drawing?.id || "drawing"
                }-${materialIndex}`,
              tagNo,
              tag: tagNo,
              components: component,
              component,
              materialDescription,
              spareParts: normalizeSpareParts(
                mergedSpecs?.spareParts ||
                  materialItem?.specs?.spec?.spareParts ||
                  [],
              ),
              dataSheet:
                // projects[1].revisions[0].data.pnidData[0].trimListTable[0].dataSheet||
                pickSpecsDataSheet(mergedSpecs) ??
                mcs?.dataSheet ??
                template?.dataSheets ??
                null,
              inspectionPlan: [],
              inspectionFindingRows: normalizeInspectionFindingRows(
                itps,
                tagNo,
              ),
            });
          });
      });
  });

  // Show rows ordered by material description first, with empty/"-"
  // descriptions pushed to the end.
  const isEmptyDescription = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized === "" || normalized === "-";
  };

  rows.sort((a, b) => {
    const aEmpty = isEmptyDescription(a.materialDescription);
    const bEmpty = isEmptyDescription(b.materialDescription);

    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;

    return String(a.materialDescription ?? "").localeCompare(
      String(b.materialDescription ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" },
    );
  });

  return rows;
}

export default function DitMaintenanceSystemTab() {
  const [value, setValue] = useState(0);

  const activeTabKey = MAINTENANCE_TABS[value]?.key;

  const { projects, itpsData, isLoading, isError, ensureAmsDataLoaded } =
    useAmsModuleData();

  useEffect(() => {
    ensureAmsDataLoaded().catch(() => {});
  }, [ensureAmsDataLoaded]);

  const rows = useMemo(() => {
    const dummyTemplates = maintenanceRegistryDummyData[activeTabKey] || [];

    return buildRowsFromProjects(
      projects,
      activeTabKey,
      dummyTemplates,
      Array.isArray(itpsData) ? itpsData : [],
    );
  }, [projects, activeTabKey, itpsData]);

  return (
    <DynamicDialogProvider>
      <Box flex={1}>
        <Tabs value={value} onChange={(_, v) => setValue(v)}>
          {MAINTENANCE_TABS.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              sx={{
                minWidth: 200,
                maxWidth: 300,
                whiteSpace: "normal",
                textAlign: "center",
                py: 1,
              }}
            />
          ))}
        </Tabs>

        <Box p={2}>
          {isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading DIT Maintenance System data.
            </Alert>
          ) : null}

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <CircularProgress size={20} />
              Loading DIT Maintenance System data...
            </Box>
          ) : null}

          <MaintenanceRegistryTable rows={rows} assetType={activeTabKey} />
        </Box>
      </Box>
    </DynamicDialogProvider>
  );
}
